import { createApi } from "@reduxjs/toolkit/query/react";
import { dynamicBaseQuery } from "@/api/DynamicBaseQuery";
import type { IApiPaginatedResponse, IApiResponse } from "@/types";
import {
  API_EXPENSE_ATTACHMENTS,
  API_EXPENSE_LOOKUPS,
  API_EXPENSE_OCR,
  API_EXPENSE_PROJECT_CATEGORY_CAPS,
  API_MS_CREATE_EXPENSE,
  API_MS_DELETE_EXPENSE,
  API_MS_EXPENSE_COMMENTS,
  API_MS_EXPENSE_STATS,
  API_MS_GET_EXPENSES,
  API_MS_GET_EXPENSE_BY_ID,
  API_MS_GET_REIMBURSEMENTS,
  API_MS_GET_REIMBURSEMENT_BY_ID,
  API_MS_REIMBURSEMENT_STATS,
  API_MS_RESUBMIT_EXPENSE,
  API_MS_UPDATE_EXPENSE,
} from "@/constants/apiEndpoints";
import type {
  IAddCommentPayload,
  ICreateExpensePayload,
  IExpense,
  IExpenseAttachment,
  IExpenseComment,
  IExpenseListItem,
  IExpenseLookups,
  IExpenseStatsData,
  IListExpensesParams,
  IListReimbursementsParams,
  IProjectCategoryCapUsage,
  IReimbursement,
  IReimbursementDetail,
  IReimbursementStatsData,
} from "@/types/(company)/expenses.interface";

export const msExpensesApi = createApi({
  reducerPath: "msExpensesApi",
  baseQuery: dynamicBaseQuery,
  refetchOnMountOrArgChange: true,
  tagTypes: [
    "msExpenses",
    "msExpense",
    "msExpenseComments",
    "msReimbursements",
    "msReimbursement",
    "expenseLookups",
  ],
  endpoints: (builder) => ({
    msGetExpenses: builder.query<
      IApiPaginatedResponse<IExpenseListItem[]>,
      IListExpensesParams
    >({
      query: (params) => ({ url: API_MS_GET_EXPENSES, params }),
      providesTags: ["msExpenses"],
    }),
    msGetExpenseStats: builder.query<IApiResponse<IExpenseStatsData>, void>({
      query: () => ({ url: API_MS_EXPENSE_STATS }),
      providesTags: ["msExpenses"],
    }),
    msGetExpenseById: builder.query<IApiResponse<IExpense>, number>({
      query: (id) => ({ url: API_MS_GET_EXPENSE_BY_ID(id) }),
      providesTags: ["msExpense"],
    }),
    msCreateExpense: builder.mutation<IApiResponse<IExpense>, ICreateExpensePayload>({
      query: (body) => ({ url: API_MS_CREATE_EXPENSE, method: "POST", body }),
      invalidatesTags: ["msExpenses"],
    }),
    msUpdateExpense: builder.mutation<
      IApiResponse<IExpense>,
      { id: number; payload: Partial<ICreateExpensePayload> }
    >({
      query: ({ id, payload }) => ({
        url: API_MS_UPDATE_EXPENSE(id),
        method: "PUT",
        body: payload,
      }),
      invalidatesTags: ["msExpenses", "msExpense"],
    }),
    msResubmitExpense: builder.mutation<IApiResponse<IExpense>, number>({
      query: (id) => ({ url: API_MS_RESUBMIT_EXPENSE(id), method: "POST" }),
      invalidatesTags: ["msExpenses", "msExpense"],
    }),
    msDeleteExpense: builder.mutation<IApiResponse<{ deleted: boolean }>, number>({
      query: (id) => ({ url: API_MS_DELETE_EXPENSE(id), method: "DELETE" }),
      invalidatesTags: ["msExpenses", "msExpense"],
    }),

    // ── Comments ──────────────────────────────────────────────────────────
    msGetExpenseComments: builder.query<IApiResponse<IExpenseComment[]>, number>({
      query: (id) => ({ url: API_MS_EXPENSE_COMMENTS(id) }),
      providesTags: ["msExpenseComments"],
    }),
    msAddExpenseComment: builder.mutation<
      IApiResponse<IExpenseComment>,
      { id: number; payload: IAddCommentPayload }
    >({
      query: ({ id, payload }) => ({
        url: API_MS_EXPENSE_COMMENTS(id),
        method: "POST",
        body: payload,
      }),
      invalidatesTags: ["msExpenseComments", "msExpense"],
    }),

    // ── Reimbursements (read-only) ──────────────────────────────────────────
    msGetReimbursements: builder.query<
      IApiPaginatedResponse<IReimbursement[]>,
      IListReimbursementsParams
    >({
      query: (params) => ({ url: API_MS_GET_REIMBURSEMENTS, params }),
      providesTags: ["msReimbursements"],
    }),
    msGetReimbursementById: builder.query<
      IApiResponse<IReimbursementDetail>,
      number
    >({
      query: (id) => ({ url: API_MS_GET_REIMBURSEMENT_BY_ID(id) }),
      providesTags: ["msReimbursement"],
    }),
    msGetReimbursementStats: builder.query<
      IApiResponse<IReimbursementStatsData>,
      void
    >({
      query: () => ({ url: API_MS_REIMBURSEMENT_STATS }),
      providesTags: ["msReimbursements"],
    }),

    // ── Scope-agnostic: lookups + attachments + OCR ─────────────────────────
    getExpenseLookups: builder.query<IApiResponse<IExpenseLookups>, void>({
      query: () => ({ url: API_EXPENSE_LOOKUPS }),
      providesTags: ["expenseLookups"],
    }),
    // A project's category caps with usage — the submit form's cap warning.
    getProjectCategoryCaps: builder.query<
      IApiResponse<IProjectCategoryCapUsage[]>,
      { projectId: number; excludeExpenseId?: number }
    >({
      query: ({ projectId, excludeExpenseId }) => ({
        url: API_EXPENSE_PROJECT_CATEGORY_CAPS(projectId),
        params: excludeExpenseId ? { excludeExpenseId } : {},
      }),
    }),
    uploadExpenseAttachments: builder.mutation<
      IApiResponse<IExpenseAttachment[]>,
      FormData
    >({
      query: (body) => ({ url: API_EXPENSE_ATTACHMENTS, method: "POST", body }),
    }),
    ocrExpenseReceipt: builder.mutation<
      IApiResponse<{ attachment: IExpenseAttachment; extracted: unknown }>,
      FormData
    >({
      query: (body) => ({ url: API_EXPENSE_OCR, method: "POST", body }),
    }),
  }),
});

export const {
  useMsGetExpensesQuery,
  useMsGetExpenseStatsQuery,
  useMsGetExpenseByIdQuery,
  useMsCreateExpenseMutation,
  useMsUpdateExpenseMutation,
  useMsResubmitExpenseMutation,
  useMsDeleteExpenseMutation,
  useMsGetExpenseCommentsQuery,
  useMsAddExpenseCommentMutation,
  useMsGetReimbursementsQuery,
  useMsGetReimbursementStatsQuery,
  useMsGetReimbursementByIdQuery,
  useGetExpenseLookupsQuery,
  useGetProjectCategoryCapsQuery,
  useUploadExpenseAttachmentsMutation,
  useOcrExpenseReceiptMutation,
} = msExpensesApi;
